import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import dashboardService from '../../services/dashboardService';




// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [projectStats, setProjectStats] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const [timeStats, setTimeStats] = useState(null);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);



  const byStatus = [
    {
      "status": "Pending",
      "count": 10
    },
    {
      "status": "In Progress",
      "count": 15
    },
    {
      "status": "Completed",
      "count": 25
    },
    {
      "status": "On Hold",
      "count": 5
    }
  ]


  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, projectData, taskData, timeData, activitiesData] = await Promise.all([
        dashboardService.getDashboardStats(),
        dashboardService.getProjectStats(),
        dashboardService.getTaskStats(),
        dashboardService.getTimeStats(),
        dashboardService.getRecentActivities(10)
      ]);

      setStats(statsData);
      setProjectStats(projectData);
      setTaskStats(taskData);
      setTimeStats(timeData);
      setActivities(activitiesData.activities || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  // Chart configurations
  const projectStatusData = {

    // labels: Object.keys(projectStats?.byStatus || {}),
    // labels: ["Test","Test-1"],
    labels: projectStats?.projects_by_status?.map(item => item.status),
    datasets: [{
      // data: Object.values(projectStats?.byStatus || {}),
      data: projectStats?.projects_by_status?.map(item => item.count),
      backgroundColor: [
        '#3B82F6', // blue
        '#10B981', // green
        '#F59E0B', // yellow
        '#EF4444', // red
        '#8B5CF6', // purple
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const taskStatusData = {
    // labels: Object.keys(taskStats?.byStatus || {}),
    // labels: ["Test","Test-1"],
    labels: taskStats?.tasks_status?.map(item => item.status),

    datasets: [{
      data: taskStats?.tasks_status?.map(item => item.count),

      // data: Object.values(taskStats?.byStatus || {}),
      // data: [10, 11],
      // data: [10, 11],
      backgroundColor: [
        '#60A5FA', // light blue
        '#34D399', // light green
        '#FBBF24', // light yellow
        '#F87171', // light red
        '#A78BFA', // light purple
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // const taskPriorityData = {
  //   // labels: Object.keys(taskStats?.tasks_status || {}),
  //   // labels: ["p1", "p2"],
  //   datasets: [{
  //     label: 'Tasks by Priority',
  //     // data: Object.values(taskStats?.byPriority || {}),
  //     data: [3, 4],
  //     backgroundColor: [
  //       '#EF4444', // red for Critical
  //       '#F59E0B', // yellow for High
  //       '#3B82F6', // blue for Medium
  //       '#10B981', // green for Low
  //     ],
  //     borderRadius: 8,
  //   }]
  // };

  const taskPriorityData = {
  labels: taskStats.byPriority.map(item => item.status),
  datasets: [
    {
      label: 'Tasks by Priority',
      data: taskStats.byPriority.map(item => item.count),
      backgroundColor: taskStats.byPriority.map(item => {
        switch (item.status) {
          case 'High':
            return '#EF4444'; // Red
          case 'Medium':
            return '#F59E0B'; // Orange
          case 'Low':
            return '#10B981'; // Green
          default:
            return '#3B82F6'; // Blue
        }
      }),
    },
  ],
};


  const d = {
    "timeStats": {
      "dailyHours": {
        "2026-05-07": 8.5,
        "2026-05-08": 7.2,
        "2026-05-09": 9.0,
        "2026-05-10": 6.5,
        "2026-05-11": 8.0,
        "2026-05-12": 7.8,
        "2026-05-13": 8.3,
        "2026-05-14": 5.5
      },
      "dailyBillable": {
        "2026-05-07": 8.5,
        "2026-05-08": 7.2,
        "2026-05-09": 9.0,
        "2026-05-10": 4.5,
        "2026-05-11": 8.0,
        "2026-05-12": 7.5,
        "2026-05-13": 8.3,
        "2026-05-14": 5.5
      },
      "totalHours": 60.8,
      "averageHoursPerDay": 7.6
    }
  }
  const timeTrackingData = {

    labels: Object.keys(d.timeStats?.dailyHours || {}).map(date => {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Total Hours',
        data: Object.values(d.timeStats?.dailyHours || {}),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Billable Hours',
        data: Object.values(d.timeStats?.dailyBillable || {}),
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12
          }
        }
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium custom-text-project">Total Projects</p>
              <p className="text-4xl font-bold mt-2">{stats?.totalProjects || 0}</p>
              <p className="text-blue-100 text-xs mt-2 custom-text-project">{stats?.activeProjects || 0} active</p>
            </div>
            <div className="text-5xl opacity-20">📁</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Total Tasks</p>
              <p className="text-4xl font-bold mt-2">{stats?.totalTasks || 0}</p>
              <p className="text-green-100 text-xs mt-2">{stats?.activeTasks || 0} active</p>
            </div>
            <div className="text-5xl opacity-20">✓</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Completion Rate</p>
              <p className="text-4xl font-bold mt-2">{stats?.completionRate || 0}%</p>
              <p className="text-purple-100 text-xs mt-2">{stats?.completedTasks || 0} completed</p>
            </div>
            <div className="text-5xl opacity-20">📊</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Hours Tracked</p>
              <p className="text-4xl font-bold mt-2">{stats?.totalHours || 0}h</p>
              <p className="text-orange-100 text-xs mt-2">{stats?.totalTimeEntries || 0} entries</p>
            </div>
            <div className="text-5xl opacity-20">⏱️</div>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Status Donut Chart */}
<div className="bg-white p-6 rounded-xl shadow-lg">
  <h2 className="text-xl font-semibold text-gray-900 mb-4">Projects by Status</h2>
  <div className="h-80 flex items-center justify-center">
    {projectStats?.projects_by_status?.length > 0 ? (
      <Doughnut data={projectStatusData} options={chartOptions} />
    ) : (
      <div className="flex flex-col items-center text-gray-400">
        <p className="text-base font-medium">No data found</p>
      </div>
    )}
  </div>
</div>

        {/* Task Status Donut Chart */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Tasks by Status</h2>
          <div className="h-80 flex items-center justify-center">
            {taskStats?.tasks_status?.length > 0 ? (
              <Doughnut data={taskStatusData} options={chartOptions} />
            ) : (
              <div className="flex flex-col items-center text-gray-400">
                <p className="text-base font-medium">No data found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Task Priority Bar Chart */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Tasks by Priority</h2>
          <div className="h-80 flex items-center justify-center">
            {taskStats?.byPriority?.length > 0 ? (
              <Bar data={taskPriorityData} options={barChartOptions} />
            ) : (
              <div className="flex flex-col items-center text-gray-400">
                <p className="text-base font-medium">No data found</p>
              </div>
            )}
          </div>
        </div>

        {/* Time Tracking Stats */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Time Tracking Overview</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Total Hours (7 days)</p>
                <p className="text-2xl font-bold text-blue-600">{timeStats?.totalHours || 0}h</p>
              </div>
              <div className="text-3xl">⏰</div>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-400 rounded-lg">
              <div>
                <p className="text-green-100 text-sm font-medium">Billable Hours</p>
                <p className="text-green-100 text-2xl font-bold">{timeStats?.billableHours || 0}h</p>
              </div>
              <div className="text-3xl">💰</div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Non-Billable Hours</p>
                <p className="text-2xl font-bold text-gray-600">{timeStats?.nonBillableHours || 0}h</p>
              </div>
              <div className="text-3xl">📝</div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Tracking Line Chart */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Time Tracking Trend (Last 7 Days)</h2>
        <div className="h-80 flex items-center justify-center">
          
          <Line data={timeTrackingData} options={chartOptions} />
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activities</h2>
        <div className="space-y-3">
          {activities.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No recent activities</p>
          ) : (
            activities.map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    activity.type === 'project' ? 'bg-primary text-primary-content' : 'bg-success text-success-content text-white'
                  }`}>
                    {activity.type === 'project' ? '📁' : '✓'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{activity.title}</p>
                    {activity.project && (
                      <p className="text-sm text-gray-500">Project: {activity.project}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${activity.status === 'Done' || activity.status === 'Completed'
                    ? 'bg-success text-success-content text-white'
                    : activity.status === 'In Progress'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                    }`}>
                    {activity.status}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(activity.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Team Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Team Members</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.teamMembers || 0}</p>
            </div>
            <div className="text-4xl">👥</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Active Projects</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.activeProjects || 0}</p>
            </div>
            <div className="text-4xl">🚀</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Active Tasks</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.activeTasks || 0}</p>
            </div>
            <div className="text-4xl">📋</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;


